import dbStorageService from '../dbStorageService';

/**
 * NotificationRegistry class to track existing notifications and prevent duplicates
 * This is used to solve the timing issue between sync and notification generation
 */
export class NotificationRegistry {
    constructor() {
        this.syncedNotifications = new Map(); // Maps type_userConfigUuid to notification
        this.deletedNotifications = new Map(); // Maps type_userConfigUuid to deletion timestamp
        this.deletionRetentionMs = 5 * 60 * 1000; // Keep deleted entries for 5 minutes
        this.initialized = false;
    }
    
    /**
     * Initialize the registry by loading all notifications from the database
     * @param {number} userId - The current user ID (optional)
     */
    async initialize(userId) {
        try {
            console.log('[NotificationRegistry] Initializing notification registry');
            // Reset the maps
            this.syncedNotifications.clear();
            this.deletedNotifications.clear();
            
            // Get all notifications from the database
            const query = userId ? 
                { index: 'userId', value: userId } : 
                { all: true };
                
            const notifications = userId ?
                await dbStorageService.getRecordsByIndex('Notification', 'userId', userId) :
                await dbStorageService.getAllRecords('Notification');
            
            // Add each notification to the registry
            let count = 0;
            let deletedCount = 0;
            const now = Date.now();
            
            for (const notification of notifications) {
                try {
                    const jsonData = typeof notification.jsonData === 'string' 
                        ? JSON.parse(notification.jsonData) 
                        : notification.jsonData;
                    
                    if (jsonData?.userConfigUuid) {
                        // Create key from type and userConfigUuid
                        const notificationType = String(notification.type);
                        const key = `${notificationType}_${jsonData.userConfigUuid}`;
                        
                        if (notification.isDeleted) {
                            // Keep track of deleted notifications to prevent immediate recreating
                            // Store deletion time for cleanup
                            this.deletedNotifications.set(key, now);
                            deletedCount++;
                        } else {
                            this.syncedNotifications.set(key, notification);
                            count++;
                        }
                    }
                } catch (error) {
                    console.error('[NotificationRegistry] Error parsing notification jsonData:', error);
                }
            }
            
            this.initialized = true;
            console.log(`[NotificationRegistry] Initialized with ${count} active and ${deletedCount} deleted tracked notifications`);
            return count;
        } catch (error) {
            console.error('[NotificationRegistry] Error initializing registry:', error);
            this.initialized = false;
            return 0;
        }
    }
    
    /**
     * Clean up old deleted notifications entries
     * @private
     */
    _cleanupDeletedEntries() {
        if (this.deletedNotifications.size === 0) return;
        
        const now = Date.now();
        const expiredKeys = [];
        
        // Find all expired entries
        for (const [key, timestamp] of this.deletedNotifications.entries()) {
            if (now - timestamp > this.deletionRetentionMs) {
                expiredKeys.push(key);
            }
        }
        
        // Remove expired entries
        for (const key of expiredKeys) {
            this.deletedNotifications.delete(key);
        }
        
        if (expiredKeys.length > 0) {
            console.log(`[NotificationRegistry] Cleaned up ${expiredKeys.length} expired deleted entries`);
        }
    }
    
    /**
     * Check if a notification exists
     * @param {string|number} type - Notification type
     * @param {string} userConfigUuid - UUID of user config associated with notification
     * @returns {boolean} Whether a notification exists
     */
    hasNotification(type, userConfigUuid) {
        if (!userConfigUuid) return false;
        
        try {
            // Clean up old deleted entries first
            this._cleanupDeletedEntries();
            
            // Ensure type is a string
            const typeStr = String(type);
            const key = `${typeStr}_${userConfigUuid}`;
            
            // Check if notification exists in synced map
            const exists = this.syncedNotifications.has(key);
            
            // Additionally check if notification was recently deleted
            const wasDeleted = this.deletedNotifications.has(key);
            
            if (wasDeleted) {
                console.log(`[NotificationRegistry] Not creating notification for type ${typeStr} and config ${userConfigUuid} because it was recently deleted`);
            }
            
            return exists || wasDeleted;
        } catch (error) {
            console.error('[NotificationRegistry] Error checking if notification exists:', error);
            return false;
        }
    }
    
    /**
     * Get a notification for a given type and userConfigUuid
     * @param {string|number} type - Notification type
     * @param {string} userConfigUuid - UUID of the userConfig
     * @returns {object|null} The notification object or null if not found
     */
    getNotification(type, userConfigUuid) {
        if (!userConfigUuid) return null;
        
        // Ensure type is a string
        const typeStr = String(type);
        const key = `${typeStr}_${userConfigUuid}`;
        return this.syncedNotifications.get(key) || null;
    }
    
    /**
     * Add a notification to the registry
     * @param {object} notification - The notification object
     * @returns {boolean} Whether the notification was added
     */
    addNotification(notification) {
        try {
            if (!notification) return false;
            
            const jsonData = typeof notification.jsonData === 'string' 
                ? JSON.parse(notification.jsonData) 
                : notification.jsonData;
                
            if (jsonData?.userConfigUuid) {
                // Ensure type is stored as a string
                const notificationType = String(notification.type);
                const key = `${notificationType}_${jsonData.userConfigUuid}`;
                
                if (notification.isDeleted) {
                    // If the notification is already deleted, add to deleted map
                    this.syncedNotifications.delete(key); // Remove from active
                    this.deletedNotifications.set(key, Date.now()); // Add to deleted
                    return true;
                } else {
                    // Remove from deleted if it was there
                    this.deletedNotifications.delete(key);
                    // Add to active
                    this.syncedNotifications.set(key, notification);
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('[NotificationRegistry] Error adding notification to registry:', error);
            return false;
        }
    }
    
    /**
     * Mark a notification as deleted in the registry
     * @param {string} uuid - UUID of the notification to mark as deleted
     * @returns {boolean} Whether the notification was found and marked
     */
    markAsDeleted(uuid) {
        try {
            // Find the notification by UUID
            for (const [key, notification] of this.syncedNotifications.entries()) {
                if (notification.uuid === uuid) {
                    // Move to deleted map
                    this.syncedNotifications.delete(key);
                    this.deletedNotifications.set(key, Date.now());
                    console.log(`[NotificationRegistry] Marked notification ${uuid} as deleted in registry`);
                    
                    // Parse jsonData to get userConfigUuid and add an additional entry by userConfigUuid
                    try {
                        const jsonData = typeof notification.jsonData === 'string' 
                            ? JSON.parse(notification.jsonData) 
                            : notification.jsonData;
                            
                        if (jsonData && jsonData.userConfigUuid) {
                            // Create key by type and userConfigUuid to prevent recreation of notification
                            const notificationType = String(notification.type);
                            const userConfigKey = `${notificationType}_${jsonData.userConfigUuid}`;
                            
                            // Also add to deleted map by userConfigUuid to prevent recreation
                            if (key !== userConfigKey) {
                                this.deletedNotifications.set(userConfigKey, Date.now());
                                console.log(`[NotificationRegistry] Also marked notification for userConfigUuid ${jsonData.userConfigUuid} as deleted in registry`);
                            }
                        }
                    } catch (error) {
                        console.error('[NotificationRegistry] Error parsing jsonData for deletion tracking:', error);
                    }
                    
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('[NotificationRegistry] Error marking notification as deleted:', error);
            return false;
        }
    }
    
    /**
     * Remove a notification from the registry
     * @param {string} uuid - UUID of the notification to remove
     * @returns {boolean} Whether the notification was removed
     */
    removeNotification(uuid) {
        try {
            // Find the notification by UUID
            for (const [key, notification] of this.syncedNotifications.entries()) {
                if (notification.uuid === uuid) {
                    this.syncedNotifications.delete(key);
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('[NotificationRegistry] Error removing notification from registry:', error);
            return false;
        }
    }
    
    /**
     * Reset the registry
     */
    reset() {
        this.syncedNotifications.clear();
        this.deletedNotifications.clear();
        this.initialized = false;
        console.log('[NotificationRegistry] Registry reset');
    }
} 